import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { useConfiguratorContext } from '@augmatica/iframe-interface-react'

import { isControllableItem } from './helpers/isControllableItem'

import {
  loadConfig,
  fetchDataForItems_aggregated,
  fetchPrices_aggregated,
  addToCart_aggregated,
  submitForm_aggregated,
} from './store/requests/thunks'

import { handleCustomer } from './store/customer/customerSlice'
import { handleFilterChangeForSelectedItem } from './store/items/itemsSlice'
import { setIsEditItemPanelActive, setIsFailedToLoadProjectModalOpen, setLoftSelected } from './store/ui/uiSlice'
import { handleAppearanceUpdate as _handleAppearanceUpdate } from './store/model/modelSlice'
import { handleSelectedItem, handleSlotOptions } from './store/selectedItem/selectedItemSlice'

import { CART, OFFER, SAVE } from './constants/constants'
import ConfiguratorView from './views/ConfiguratorView'
import ErrorView from './views/ErrorView'
import AppHeader from './components/AppHeader/AppHeader'
import Faq from './components/Faq/Faq'
import Message from './components/Message/Message'
import SaveProject from './components/SaveProject/SaveProject'
import UserGuide from './components/UserGuide/UserGuide'
import * as Styled from './styles'
import { useAppDispatch, useAppSelector } from './hooks'
import type * as DynamakerContract from './integration/DynamakerContract'

const faqElement = document.querySelector('#mok-context-spa-banners > .faq')
let faqHTML = null
if (faqElement) {
  faqHTML = faqElement.outerHTML
  faqElement.remove()
}

const userGuideElement = document.querySelector('#mok-context-spa-banners > #user-guide')
let userGuideHTML = null
if (userGuideElement) {
  userGuideHTML = userGuideElement.outerHTML
  userGuideElement.remove()
}

const App: React.FC = () => {
  const { showAnnotations } = useAppSelector((state) => state.model)
  const { isEditItemPanelActive, isSummaryPanelVisible } = useAppSelector((state) => state.ui)
  const { selectedItem } = useAppSelector((state) => state.selectedItem)
  const { configId: configIdFromYass, configData: configDataFromYass } = useAppSelector((state) => state.yass)

  const showAnnotationsRef = useRef(showAnnotations)

  const [isDmAppReady, _setIsDmAppReady] = useState(false)
  const [isDmLoadedProjectReady, _setIsDmLoadedProjectReady] = useState(false)
  const setIsDmAppReady = (value: boolean): void => {
    isDmAppReadyRef.current = value
    _setIsDmAppReady(value)
  }
  const setIsDmLoadedProjectReady = (value: boolean): void => {
    isDmLoadedProjectReadyRef.current = value
    _setIsDmLoadedProjectReady(value)
  }

  const isDmAppReadyRef = useRef(isDmAppReady)
  const isDmLoadedProjectReadyRef = useRef(isDmLoadedProjectReady)
  // const configurationUpdateCountRef = useRef(0)
  const configurator = useConfiguratorContext()
  const configuratorRef = useRef(configurator)

  const dispatch = useAppDispatch()
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), [])
  const configIdFromUrl = urlParams.get('configId') ?? urlParams.get('configid')
  const configHashKeyFromUrl = urlParams.get('configHashKey') ?? urlParams.get('confighashkey')?.toUpperCase()
  const initWithDefaultProject = !configIdFromUrl

  useEffect(() => {
    configuratorRef.current = configurator
  }, [configurator])

  const handleConfigResult = useCallback(
    (configResult: Omit<DynamakerContract.ConfigResult, 'slotOptions'>) => {
      const itemsList = Object.values(configResult.items)
      const articlesInConfig = new Set<string>()
      // @ts-expect-error //TODO 5625: remove
      configResult.bom = configResult.configurationSections // TODO 5625
      configResult.bom.forEach((section) =>
        section.articles.forEach((article) => articlesInConfig.add(article.erpItemNumber))
      )
      dispatch(
        fetchDataForItems_aggregated({
          items: ([] as (typeof itemsList)[0]).concat(...itemsList),
          // @ts-expect-error //TODO 5625: remove
          articlesInConfig: configResult.bom.reduce((skus, item) => skus.add(item.sku), new Set<string>()),
        })
      ).then(() => {
        dispatch(fetchPrices_aggregated(configResult))
          .unwrap()
          .then(
            ({
              bomItemsWithUpdatedConfigData,
            }: {
              bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
            }) => {
              if (bomItemsWithUpdatedConfigData.length) {
                configurator.updateItems(
                  bomItemsWithUpdatedConfigData.map((bomItem) => ({
                    item: bomItem.item,
                    id: bomItem.id,
                    props: { configData: bomItem.configData } as any,
                  })),
                  { silent: true }
                )
              }
            }
          )
      })
    },
    [configurator, dispatch]
  )

  useEffect(() => {
    const onItemSelected = (data: { id: string }): void => {
      if (isEditItemPanelActive) {
        if (data.id !== selectedItem.id) {
          dispatch(setIsEditItemPanelActive(false))
          configurator.cameraToPrevious()
        }
      }
    }

    configurator.addEventListener('item-selected', onItemSelected)

    return () => {
      // @ts-expect-error // TODO
      configurator.removeEventListener('item-selected', onItemSelected)
    }
  }, [selectedItem, isEditItemPanelActive])

  useEffect(() => {
    const handleAppearanceUpdate = (appearance: DynamakerContract.Appearance): void => {
      if (appearance.showAnnotations !== showAnnotationsRef.current) {
        showAnnotationsRef.current = appearance.showAnnotations
        dispatch(_handleAppearanceUpdate(appearance))
      }
    }

    const handleMessageForUpdateEvent = (event: MessageEvent): void => {
      if (event.origin === 'https://deployed.dynamaker.com') {
        const { event: dmEvent, data: dmData } = event.data

        // On configuration update from DM application
        if (dmEvent === 'configuration-update') {
          // ++configurationUpdateCountRef.current

          const { slotOptions, ...configResult } = dmData as DynamakerContract.ConfigurationUpdate['data']

          dispatch(handleSlotOptions({ slotOptions, _dmEvent: dmEvent }))

          if (isDmLoadedProjectReadyRef.current || (initWithDefaultProject && isDmAppReadyRef.current)) {
            handleConfigResult(configResult)
          }

          return
        }

        // On item selected in DM application
        if (dmEvent === 'item-selected') {
          const { slotOptions, ...selectedItem } = dmData as DynamakerContract.ItemSelected['data']
          dispatch(handleSlotOptions({ slotOptions }))
          dispatch(handleSelectedItem(selectedItem))

          if (isControllableItem(selectedItem)) {
            dispatch(handleFilterChangeForSelectedItem(selectedItem))
          }

          return
        }

        // On model appearance update in DM application
        if (dmEvent === 'appearance-update') {
          const appearance = dmData as DynamakerContract.AppearanceUpdate['data']

          // note: action is dispatched within handleAppearanceUpdate()
          handleAppearanceUpdate(appearance)
        }
      }
    }

    const promises: any[] = []

    if (isDmAppReady) {
      // TODO--dec: eventlyssnare på 'configurator' istället?
      window.addEventListener('message', handleMessageForUpdateEvent)

      const sibhcFromUrl = urlParams.get('sibhc')
      if (sibhcFromUrl) {
        dispatch(handleCustomer({ cnohash: sibhcFromUrl }))
      }

      if (configIdFromUrl && configHashKeyFromUrl) {
        promises.push(
          dispatch(
            loadConfig({
              configId: configIdFromUrl,
              configHashKey: configHashKeyFromUrl,
            })
          )
        )
      }
    }

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessageForUpdateEvent)

      promises.forEach((promise) => {
        promise.abort()
      })
    }
  }, [dispatch, isDmAppReady])

  useEffect(() => {
    if (configDataFromYass && isDmAppReady) {
      configurator
        .loadProject(configDataFromYass)
        .then((dmData) => {
          const { slotOptions, ...configResult } = dmData as DynamakerContract.ConfigResult
          setIsDmLoadedProjectReady(true)
          dispatch(setLoftSelected(!!configResult.props.loftLeft.value || !!configResult.props.loftRight.value))
          dispatch(handleSlotOptions({ slotOptions }))
          handleConfigResult(configResult)
        })
        .catch((error) => {
          console.error(`DM loadProject was rejected (failed), configId (from YASS): ${configIdFromYass}`)
          console.error(error)
          setIsFailedToLoadProjectModalOpen(true)
        })
    }
  }, [configDataFromYass, isDmAppReady])

  useEffect(() => {
    const handleMessageForApplicationEvent = (event: MessageEvent): void => {
      if (event.origin === 'https://deployed.dynamaker.com') {
        const { event: dmEvent, data: dmData } = event.data

        // When DM application is ready
        if (dmEvent === 'application-ready') {
          setIsDmAppReady(true)

          return
        }

        // On DM application error
        if (dmEvent === 'error') {
          console.error('Error event from DM:', JSON.stringify(dmData))
        }
      }
    }

    // TODO--dec: eventlyssnare på 'configurator' istället?
    window.addEventListener('message', handleMessageForApplicationEvent)

    // Cleanup
    return () => {
      window.removeEventListener('message', handleMessageForApplicationEvent)
    }
  }, [])

  // useEffect(() => {
  //   // TODO ts?
  //   const beforeUnloadHandler = (event: { target: any; preventDefault: () => void; returnValue: boolean }) => {
  //     if (
  //       configurationUpdateCountRef.current > 1 && // if user has done any change to the configuration and ...
  //       event.target.activeElement?.id !== 'to-checkout-button' // ... the 'to checkout' button was _not_ clicked
  //     ) {
  //       // Trigger a browser-generated confirmation dialog
  //       event.preventDefault()

  //       // Included for legacy support, e.g. Chrome/Edge < 119
  //       event.returnValue = true
  //     }
  //   }

  //   window.addEventListener('beforeunload', beforeUnloadHandler)

  //   // Cleanup
  //   return () => {
  //     window.removeEventListener('beforeunload', beforeUnloadHandler)
  //   }
  // }, [])

  useEffect(() => {
    if (isSummaryPanelVisible) {
      configuratorRef.current.setMode('view')
    } else {
      configuratorRef.current.setMode('configure')
    }
  }, [isSummaryPanelVisible])

  const forceDmTestEnv = urlParams.get('dmEnv') === 'test'
  const enableDmTestTab = urlParams.get('dmTestTab') === 'enable'

  if ((configIdFromUrl && !configHashKeyFromUrl) || (!configIdFromUrl && configHashKeyFromUrl)) {
    console.warn('configId or configHashKey is missing in urlParams')
    return <ErrorView />
  }

  const isIframeReady = isDmAppReady && (isDmLoadedProjectReady || initWithDefaultProject)

  const onRequestSaveProject = ({ type, customer }: any): { internalSaveUuidv4: string } => {
    if (type !== CART && type !== OFFER && type !== SAVE) {
      console.warn("Unexpected or missing value for 'type' in App component")
    }

    const internalSaveUuidv4 = uuidv4()

    if (customer) {
      dispatch(handleCustomer(customer))
    }

    configurator
      .saveProject()
      .then((projectData: DynamakerContract.SavefileReady['data']) => {
        //@ts-expect-error // TODO 5625 remove
        const { config, configurationSections: bom, meta, props, items } = projectData
        dispatch(
          type === CART
            ? addToCart_aggregated({
                config,
                bom,
                meta,
                props,
                items,
                internalSaveUuidv4,
              })
            : submitForm_aggregated({
                config,
                bom,
                meta,
                props,
                items,
                internalSaveUuidv4,
                type,
              })
        )
          .unwrap()
          .then(
            ({
              bomItemsWithUpdatedConfigData,
            }: {
              bomItemsWithUpdatedConfigData: DynamakerContract.ConfiguredArticleQty[]
            }) => {
              configurator.updateItems(
                bomItemsWithUpdatedConfigData.map((bomItem) => ({
                  item: bomItem.item,
                  id: bomItem.id,
                  props: { configData: bomItem.configData } as any,
                })),
                { silent: false } // will cause a 'configuration-update' event from DM which will dispatch the action 'fetchPrices_aggregated'
              )
            }
          )
          .catch((error) => {
            console.error(error)
          })
      })
      .catch((error) => {
        console.error(error)
      })

    return { internalSaveUuidv4 }
  }

  return (
    <Styled.App>
      <AppHeader
        onRequestSaveProject={onRequestSaveProject} // Add to cart
      />

      <ConfiguratorView
        forceDmTestEnv={forceDmTestEnv}
        enableDmTestTab={enableDmTestTab}
        awaitProjectLoaded={!initWithDefaultProject}
        isIframeReady={isIframeReady}
        onRequestSaveProject={onRequestSaveProject}
      />

      <SaveProject onRequestSaveProject={onRequestSaveProject} />
      {faqHTML && <Faq faqHTML={faqHTML} />}
      {userGuideHTML && <UserGuide userGuideHTML={userGuideHTML} />}

      <Message />
    </Styled.App>
  )
}

export default App
